<script setup>
import { ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useMessageContext } from '../provider.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const showGallery = ref(false);

const { filteredCurrentChatAttachments } = useMessageContext();

// Carrega com auto-retry (máx 3, mesmo padrão do bubbles/Image.vue). Cobre a corrida em
// que o preview é pedido antes de o blob estar servível — o "Falha no carregamento" que
// aparecia no envio e sumia no reload. Reexecuta ao trocar a dataUrl (render otimista ->
// URL confirmada). Se ainda assim esgotar, o estado de erro vira BOTÃO de tentar de novo,
// recuperando sem recarregar a página (evita ticket de suporte).
const { isLoaded, hasError, isRetrying, loadWithRetry, reload } =
  useLoadWithRetry();

watch(
  () => props.attachment?.dataUrl,
  url => {
    if (url) loadWithRetry(url);
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="showGallery = true"
  >
    <button
      v-if="hasError"
      type="button"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11 disabled:cursor-wait"
      :disabled="isRetrying"
      @click.stop="reload"
    >
      <Icon
        :icon="isRetrying ? 'i-lucide-loader-circle' : 'i-lucide-rotate-cw'"
        :class="{ 'animate-spin': isRetrying }"
        class="text-n-slate-11"
      />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </button>
    <img
      v-else-if="isLoaded"
      class="object-cover w-full h-full skip-context-menu"
      :src="attachment.dataUrl"
    />
    <div v-else class="flex items-center justify-center size-full bg-n-alpha-1">
      <Icon
        icon="i-lucide-loader-circle"
        class="animate-spin text-n-slate-10"
      />
    </div>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="hasError = true"
    @close="() => (showGallery = false)"
  />
</template>
